import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import MowLogo from '../../assets/logos/mow-logo.svg';
import MowLogoRound from '../../assets/logos/mow-logo-round.svg';
import ProductInventoryIcon from '../../assets/icons/sidebar/product-inventory-icon.svg';
import BulkWineIcon from '../../assets/icons/sidebar/bulk-wine-icon.svg';
import CleanskinIcon from '../../assets/icons/sidebar/cleanskin-icon.svg';
import FruitIcon from '../../assets/icons/sidebar/fruit-icon.svg';
import SuppliersIcon from '../../assets/icons/sidebar/suppliers-icon.svg';
import VineyardsIcon from '../../assets/icons/sidebar/vineyards-icon.svg';
import UsersIcon from '../../assets/icons/sidebar/users-icon.svg';
import LogoutIcon from '../../assets/icons/sidebar/logout-icon.svg';
import { authService } from '../../services/auth.service';

// Menu visibility toggles (set to true/false to show/hide menu items)
const showProductInventory = false // true;
const showBulkWine = true;
const showCleanskin = false; //true;
const showFruit = false; //true;
const showSuppliers = true;
const showVineyards = false;// true;
const showUsers = true;

interface MenuItemProps {
  label: string;
  path: string;
  icon?: string;
  children?: MenuItemProps[];
  requiredRole?: string;
  hiddenRoles?: string[];
  visible?: boolean;
}

const menuItems: MenuItemProps[] = [
  {
    label: 'Product Inventory',
    path: '/inventory',
    icon: ProductInventoryIcon,
    visible: showProductInventory,
    children: [
      { label: 'Bulk Wine', path: '/inventory/bulk-wine', icon: BulkWineIcon, visible: showBulkWine },
      { label: 'Cleanskin', path: '/inventory/cleanskin', icon: CleanskinIcon, visible: showCleanskin },
      { label: 'Fruit', path: '/inventory/fruit', icon: FruitIcon, visible: showFruit },
    ]
  },
  { label: 'Suppliers', path: '/suppliers', icon: SuppliersIcon, requiredRole: 'superadmin', visible: showSuppliers },
  { label: 'Vineyards', path: '/vineyards', icon: VineyardsIcon, visible: showVineyards },
  { label: 'Users', path: '/users', icon: UsersIcon, visible: showUsers, hiddenRoles: ['supplier_user'] },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  
  // Load user data synchronously to prevent flash
  const getUserData = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse user data:', e);
        return null;
      }
    }
    return null;
  };
  
  const user = getUserData();

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  return (
    <aside className="flex flex-col bg-[#E3DED9] h-screen" style={{
      width: '280px'
    }}>
      {/* Logo */}
      <div className="p-6 flex-shrink-0">
        <img src={MowLogo} alt="Ministry of Wine" className="h-12" />
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto px-4 py-2">
        {menuItems.map((item) => {
          // If parent is disabled, render children as top-level items
          if (item.visible === false && item.children) {
            return (
              <React.Fragment key={item.path}>
                {item.children
                  .filter(child => child.visible !== false)
                  .map((child) => {
                    const isChildActive = location.pathname === child.path;
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`flex items-center ${
                          isChildActive
                            ? 'bg-[#404040] text-white'
                            : 'bg-[#E3DED9] text-neutral-700 hover:bg-neutral-300'
                        }`}
                        style={{
                          borderRadius: '6px',
                          width: '216px',
                          padding: '8px 12px',
                          gap: '12px',
                          marginBottom: '8px'
                        }}
                      >
                        {child.icon && (
                          <img
                            src={child.icon}
                            alt=""
                            className={`w-5 h-5 flex-shrink-0 ${isChildActive ? 'brightness-0 invert' : ''}`}
                          />
                        )}
                        <span className="font-medium">{child.label}</span>
                      </Link>
                    );
                  })}
              </React.Fragment>
            );
          }

          // Only render visible items (with role check)
          if (
            item.visible === false ||
            (item.requiredRole && user?.role !== item.requiredRole) ||
            item.hiddenRoles?.includes(user?.role)
          ) {
            return null;
          }

          const isActive = location.pathname === item.path;
          return (
            <div key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center ${
                  isActive
                    ? 'bg-[#404040] text-white'
                    : 'bg-[#E3DED9] text-neutral-700 hover:bg-neutral-300'
                }`}
                style={{
                  borderRadius: '6px',
                  width: '216px',
                  padding: '8px 12px',
                  gap: '12px',
                  marginBottom: '8px'
                }}
              >
                {item.icon && (
                  <img
                    src={item.icon}
                    alt=""
                    className={`w-5 h-5 flex-shrink-0 ${isActive ? 'brightness-0 invert' : ''}`}
                  />
                )}
                <span className="font-medium">{item.label}</span>
              </Link>

              {item.children && item.children.filter(child => child.visible !== false).map((child) => {
                const isChildActive = location.pathname === child.path;
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    className={`flex items-center ${
                      isChildActive
                        ? 'bg-[#404040] text-white'
                        : 'bg-[#E3DED9] text-neutral-700 hover:bg-neutral-300'
                    }`}
                    style={{
                      borderRadius: '6px',
                      width: '216px',
                      padding: '8px 12px',
                      gap: '12px',
                      marginBottom: '8px',
                      marginLeft: '24px'
                    }}
                  >
                    {child.icon && (
                      <img
                        src={child.icon}
                        alt=""
                        className={`w-5 h-5 flex-shrink-0 ${isChildActive ? 'brightness-0 invert' : ''}`}
                      />
                    )}
                    <span className="font-medium">{child.label}</span>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User/Logout Footer */}
      <div className="flex-shrink-0" style={{ 
        padding: '24px',
        borderTop: '0.5px solid rgba(64, 64, 64, 0.5)' 
      }}>
        <div className="flex items-center gap-3">
          <Link to="/profile" className="flex items-center gap-3 hover:opacity-80 transition-opacity" style={{ flex: 1, minWidth: 0, textDecoration: 'none' }}>
            <img src={MowLogoRound} alt="User" className="w-11 h-11" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <p style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '21px',
                color: '#101828',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>{user?.name || 'Admin User'}</p>
              <p style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '21px',
                color: '#667085',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>{user?.email || 'admin@yards.com'}</p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center hover:opacity-70 transition-opacity"
            style={{ 
              width: '24px', 
              height: '24px',
              flexShrink: 0
            }}
            title="Logout"
          >
            <img 
              src={LogoutIcon} 
              alt="Logout" 
              style={{ width: '20px', height: '20px', display: 'block' }}
            />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
